import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { collection, doc, documentId, onSnapshot, query, where, updateDoc } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { CreativeSession, CreativeStudentWork, Squad, User } from '../../../types';
import { useAuth } from '../../auth/contexts/AuthContext';

interface StudentWorkWithUser extends CreativeStudentWork {
  id: string;
  student?: User;
}

const OGCreateTeacherView: React.FC = () => {
  const params = useParams();
  const squadId = params.squadId as string | undefined;
  const { currentUser } = useAuth();
  const [squad, setSquad] = useState<Squad | null>(null);
  const [session, setSession] = useState<CreativeSession | null>(null);
  const [studentMap, setStudentMap] = useState<Record<string, User>>({});
  const [studentWork, setStudentWork] = useState<StudentWorkWithUser[]>([]);
  const [copiedStudentId, setCopiedStudentId] = useState<string | null>(null);
  const [hoveredStudentId, setHoveredStudentId] = useState<string | null>(null);
  const [activeStudentId, setActiveStudentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!squadId) return;

    const squadRef = doc(db, 'squads', squadId);
    const unsubscribe = onSnapshot(squadRef, (snap) => {
      if (!snap.exists()) {
        setError('Squad not found.');
        setSquad(null);
        setLoading(false);
        return;
      }
      const data = { ...(snap.data() as any), id: snap.id } as Squad;
      setSquad(data);
      setError(null);
    }, (err) => {
      console.error('Failed to load squad for OG Create view', err);
      setError('Failed to load squad.');
    });

    return () => unsubscribe();
  }, [squadId]);

  // Load teacher-led creative session for this squad (status === 'active')
  useEffect(() => {
    if (!squadId) return;

    const sessionsQuery = query(
      collection(db, 'creativeSessions'),
      where('squadId', '==', squadId),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(sessionsQuery, (snap) => {
      if (snap.empty) {
        setSession(null);
        setStudentWork([]);
        setLoading(false);
        return;
      }
      // Expect at most one active session per squad; take the first
      const docSnap = snap.docs[0];
      const data = docSnap.data() as any;
      setSession({
        id: docSnap.id,
        squadId: data.squadId,
        teacherId: data.teacherId,
        type: data.type || 'prompting',
        startTime: data.startTime,
        endTime: data.endTime,
        status: data.status,
        missionText: data.missionText,
        constraints: data.constraints,
      });
      setLoading(false);
    }, (err) => {
      console.error('Failed to load creative session for OG Create view', err);
      setError('Failed to load creative session.');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [squadId]);

  // Load students for display (names, muses)
  useEffect(() => {
    if (!squad?.studentIds || squad.studentIds.length === 0) {
      setStudentMap({});
      return;
    }

    const studentsQuery = query(
      collection(db, 'users'),
      where(documentId(), 'in', squad.studentIds)
    );

    const unsubscribe = onSnapshot(studentsQuery, (snap) => {
      const map: Record<string, User> = {};
      snap.docs.forEach((d) => {
        const user = { ...(d.data() as any), id: d.id } as User;
        map[user.id] = user;
      });
      setStudentMap(map);
    }, (err) => {
      console.error('Failed to load students for OG Create view', err);
    });

    return () => unsubscribe();
  }, [squad?.studentIds]);

  // Subscribe to student work inside the active creative session
  useEffect(() => {
    if (!session) {
      return;
    }

    const workRef = collection(db, 'creativeSessions', session.id, 'studentWork');
    const unsubscribe = onSnapshot(workRef, (snap) => {
      const work: StudentWorkWithUser[] = snap.docs.map((d) => {
        const data = d.data() as any;
        const student = studentMap[data.studentId];
        return {
          id: d.id,
          studentId: data.studentId,
          squadId: data.squadId,
          sessionId: session.id,
          promptText: data.promptText || '',
          status: data.status || 'draft',
          teacherFeedback: data.teacherFeedback,
          updatedAt: data.updatedAt,
          student,
        };
      });
      setStudentWork(work);
    }, (err) => {
      console.error('Failed to load student work for OG Create view', err);
    });

    return () => unsubscribe();
  }, [session, studentMap]);

  const handleCopyPrompt = async (promptText: string, studentName: string, studentId: string) => {
    try {
      const textToCopy = promptText || '';
      if (!textToCopy) {
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback: best-effort copy using a temporary textarea
        const el = document.createElement('textarea');
        el.value = textToCopy;
        el.style.position = 'fixed';
        el.style.top = '0';
        el.style.left = '0';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.focus();
        el.select();
        try {
          document.execCommand('copy');
        } finally {
          document.body.removeChild(el);
        }
      }
      console.log(`Copied OG Create prompt for ${studentName} to clipboard.`);
      setCopiedStudentId(studentId);
      setTimeout(() => {
        setCopiedStudentId((current) => (current === studentId ? null : current));
      }, 900);
    } catch (e) {
      console.error('Failed to copy OG Create prompt to clipboard', e);
    }
  };

  const handleNextPrompt = async (sessionId: string, studentId: string) => {
    try {
      const workRef = doc(db, 'creativeSessions', sessionId, 'studentWork', studentId);
      await updateDoc(workRef, { status: 'draft' });
      console.log('Reset OG Create prompt status to draft for', { sessionId, studentId });
    } catch (e) {
      console.error('Failed to reset prompt status for next round', e);
    }
  };

  const sortedWork = useMemo(() => {
    return [...studentWork].sort((a, b) => {
      const nameA = (a.student?.displayName || '').toLowerCase();
      const nameB = (b.student?.displayName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [studentWork]);

  if (!currentUser) {
    return <div style={{ padding: 24 }}>You must be signed in to view OG Create.</div>;
  }

  if (loading) {
    return <div style={{ padding: 24 }}>Loading OG Create session...</div>;
  }

  if (error) {
    return <div style={{ padding: 24, color: '#b91c1c' }}>{error}</div>;
  }

  if (!squad) {
    return <div style={{ padding: 24 }}>Squad not found.</div>;
  }

  const isAICurriculum = typeof squad.curriculumId === 'string'
    && squad.curriculumId.toLowerCase().startsWith('ai');

  return (
    <div style={{ padding: '24px 32px', fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif' }}>
      <h1 style={{ fontSize: '2.25rem', marginBottom: 8 }}>
        OG Create – {squad.title}
      </h1>
      {!isAICurriculum && (
        <p style={{ marginBottom: 16, color: '#b45309' }}>
          This squad is not using an AI curriculum. OG Create is currently designed for AI classes.
        </p>
      )}
      {session ? (
        <p style={{ marginBottom: 16, color: '#4b5563' }}>
          Live creative session <strong>{session.id}</strong> is <strong>{session.status}</strong>.
          {' '}Students opening <strong>OG Creators (Beta)</strong> will appear here as they type.
        </p>
      ) : (
        <p style={{ marginBottom: 16, color: '#4b5563' }}>
          No active OG Create session is currently running for this squad.
          Start a level in this AI class to begin a new creative session.
        </p>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '16px',
          marginTop: 24,
        }}
      >
        {sortedWork.length === 0 && (
          <div style={{ gridColumn: '1 / -1', color: '#6b7280' }}>
            No student prompts yet. Ask students to open <strong>OG Creators (Beta)</strong> from their Games Collection and start typing.
          </div>
        )}

        {sortedWork.map((work) => {
          const student = work.student;
          const museImg =
            student?.selectedMuse && typeof student.selectedMuse === 'object'
              ? (student.selectedMuse as any).img
              : null;
          const displayName = student?.displayName || 'Student';

          return (
            <div
              key={work.id}
              style={{
                background: '#ffffff',
                borderRadius: 16,
                padding: 16,
                boxShadow: '0 4px 18px rgba(15, 23, 42, 0.08)',
                border: '2px solid #e0f2fe',
                minHeight: 180,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {work.status === 'submitted' && (
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '999px',
                        background: '#bbf7d0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        color: '#166534',
                      }}
                      title="Student marked as done"
                    >
                      ✓
                    </div>
                  )}
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      overflow: 'hidden',
                      background: '#eff6ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 10,
                    }}
                  >
                    {museImg ? (
                      <img
                        src={museImg}
                        alt={displayName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span role="img" aria-label="student">
                        🎵
                      </span>
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{displayName}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase' }}>
                      {work.status || 'draft'}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopyPrompt(work.promptText, displayName, work.studentId)}
                  onMouseEnter={() => setHoveredStudentId(work.studentId)}
                  onMouseLeave={() => {
                    setHoveredStudentId((current) =>
                      current === work.studentId ? null : current
                    );
                    setActiveStudentId((current) =>
                      current === work.studentId ? null : current
                    );
                  }}
                  onMouseDown={() => setActiveStudentId(work.studentId)}
                  onMouseUp={() =>
                    setActiveStudentId((current) =>
                      current === work.studentId ? null : current
                    )
                  }
                  style={{
                    border: 'none',
                    borderRadius: 999,
                    padding: '6px 12px',
                    background:
                      activeStudentId === work.studentId
                        ? '#1d4ed8'
                        : hoveredStudentId === work.studentId
                        ? '#bfdbfe'
                        : '#e0f2fe',
                    color: activeStudentId === work.studentId ? '#ffffff' : '#1d4ed8',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    flexShrink: 0,
                    transition: 'background-color 120ms ease-out, color 120ms ease-out',
                  }}
                  title="Copy prompt to clipboard"
                >
                  <span role="img" aria-label="copy">
                    📋
                  </span>
                  Copy
                </button>
              </div>

              <div
                style={{
                  flex: 1,
                  background: '#f9fafb',
                  borderRadius: 12,
                  padding: 12,
                  fontSize: 14,
                  color: '#111827',
                  whiteSpace: 'pre-wrap',
                  overflowY: 'auto',
                  maxHeight: 240,
                  marginBottom: 8,
                }}
              >
                {work.promptText || (
                  <span style={{ color: '#9ca3af' }}>Student has not started typing yet.</span>
                )}
              </div>

              {session && (work.status === 'submitted' || work.status === 'ready') && (
                <div style={{ marginTop: 4, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => handleNextPrompt(session.id, work.studentId)}
                    style={{
                      border: 'none',
                      borderRadius: 999,
                      padding: '6px 14px',
                      background: '#111827',
                      color: '#f9fafb',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>⏭</span>
                    <span>Next Prompt</span>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OGCreateTeacherView;


